import { useApiContext } from '@/contexts/apiContext';
import { useEffect, useState } from 'react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import ProgressIndicator from '../progressIndicator';
import PdfViewer from './pdfViewer';

type Props = {
  dataSourceDefId: string;
  dataSourceInstanceId: string;
  dataSourceItemId: string;
  filename: string;
  releventPages?: number[];
  maxHeight?: number;
  showLabel?: boolean;
  eventId?: string;
  dataSourceInstance?: any;
  parentItemName?: string;
  pipelineInstanceId?: string;
};

const PdfDataSourceItemViewer = ({
  dataSourceDefId,
  dataSourceInstanceId,
  dataSourceItemId,
  filename,
  releventPages = [],
  maxHeight,
  showLabel,
  eventId,
  dataSourceInstance,
  parentItemName,
  pipelineInstanceId,
}: Props) => {
  const apiContext = useApiContext();

  const [documentContent, setDocumentContent] = useState<any>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const downloadDocument = async () => {
      setError(null);
      try {
        setLoading(true);

        const meta = dataSourceInstance?.dataSourceDef?.metadata || {};
        const getFromSourceDirectly =
          meta.hasParentChildItems === true && meta.publishParentItemEvents === false && !parentItemName;

        if (getFromSourceDirectly) {
          const data = await apiContext.getDataSourceItemContentDirectFromSource(
            dataSourceDefId,
            dataSourceInstanceId,
            filename || dataSourceItemId,
            true,
          );
          const blob = new Blob([data], { type: 'application/pdf' });
          const objectUrl = URL.createObjectURL(blob);
          setDocumentContent(objectUrl);
          return;
        }

        const { content } = await apiContext.getDataSourceInstanceItem(
          dataSourceDefId,
          dataSourceInstanceId,
          dataSourceItemId,
          true,
          eventId,
          pipelineInstanceId,
        );
        const decoder = new TextDecoder('utf-8');
        let isJson = false;
        try {
          const textContent = JSON.parse(decoder.decode(content));
          if (textContent.type && textContent.content) {
            let base64String = '';
            if (textContent.type === 'Base64Encoded') {
              base64String = textContent.content;
            } else {
              base64String = btoa(textContent.content);
            }
            setDocumentContent(`data:application/pdf;base64,${base64String}`);
            isJson = true;
          }
        } catch (e) {
          // Ignore JSON parse error, fallback to binary Blob
        }

        if (!isJson) {
          const blob = new Blob([content], { type: 'application/pdf' });
          const objectUrl = URL.createObjectURL(blob);
          setDocumentContent(objectUrl);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (dataSourceDefId && dataSourceInstanceId && dataSourceItemId) {
      downloadDocument();
    }
  }, [dataSourceDefId, dataSourceInstanceId, dataSourceItemId, eventId]);

  if (loading) {
    return <ProgressIndicator />;
  }
  return (
    <PdfViewer
      pdfDataUrl={documentContent}
      showLabel={showLabel}
      label={filename}
      error={error}
      releventPages={releventPages}
      maxHeight={maxHeight}
    />
  );
};

export default PdfDataSourceItemViewer;
